/**
 * Centralized Logger Utility
 * 
 * Provides consistent logging across the application with environment-aware
 * logging levels and integration with error reporting.
 */

// Lazy import environmentConfig to avoid circular dependency (environmentConfig imports logger)
// import { isDevelopment } from './environmentConfig';
// Lazy import errorReporting to avoid circular dependency (errorReporting imports logger)
// import { errorReporting } from './errorReporting';

// Log Levels
export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

/**
 * Logger class for centralized logging
 */
class Logger {
  constructor() {
    // Lazy load isDevelopment to avoid circular dependency
    let isDev = false;
    try {
      // Check for Vite development mode (safely handle import.meta.env)
      if (import.meta.env && import.meta.env.DEV) {
        isDev = true;
      } else {
        // Check for localhost or development URLs
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        isDev = hostname === 'localhost' || 
                hostname === '127.0.0.1' || 
                hostname.startsWith('192.168.') ||
                hostname.endsWith('.local');
      }
    } catch (error) {
      // Fallback to false if we can't determine
      isDev = false;
    }
    this.logLevel = isDev ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO;
    this.enabled = true;
    this.prefix = '[Emmy\'s Learning]';
  }

  /**
   * Set log level
   */
  setLevel(level) {
    if (Object.values(LOG_LEVELS).includes(level)) {
      this.logLevel = level;
    }
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Check if a log level should be output
   */
  shouldLog(level) {
    if (!this.enabled) return false;

    const levelPriority = {
      [LOG_LEVELS.DEBUG]: 0,
      [LOG_LEVELS.INFO]: 1,
      [LOG_LEVELS.WARN]: 2,
      [LOG_LEVELS.ERROR]: 3
    };

    const currentPriority = levelPriority[this.logLevel] || 0;
    const messagePriority = levelPriority[level] || 0;

    return messagePriority >= currentPriority;
  }

  /**
   * Format log message
   */
  formatMessage(level, ...args) {
    const timestamp = new Date().toISOString();
    return [`${this.prefix} [${level.toUpperCase()}] [${timestamp}]`, ...args];
  }

  /**
   * Debug level logging (development only)
   */
  debug(...args) {
    if (!this.shouldLog(LOG_LEVELS.DEBUG)) return;
    
    // Check if development mode (inline to avoid circular dependency)
    let isDev = false;
    try {
      if (import.meta.env && import.meta.env.DEV) {
        isDev = true;
      } else if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        isDev = hostname === 'localhost' || 
                hostname === '127.0.0.1' || 
                hostname.startsWith('192.168.') ||
                hostname.endsWith('.local');
      }
    } catch (error) {
      // Fallback
    }
    
    if (isDev) {
      console.log(...this.formatMessage(LOG_LEVELS.DEBUG, ...args));
    }
  }

  /**
   * Info level logging
   */
  info(...args) {
    if (!this.shouldLog(LOG_LEVELS.INFO)) return;
    
    console.log(...this.formatMessage(LOG_LEVELS.INFO, ...args));
  }

  /**
   * Warn level logging
   */
  warn(...args) {
    if (!this.shouldLog(LOG_LEVELS.WARN)) return;
    
    console.warn(...this.formatMessage(LOG_LEVELS.WARN, ...args));
  }

  /**
   * Error level logging (always enabled)
   */
  error(...args) {
    // Errors are always logged
    console.error(...this.formatMessage(LOG_LEVELS.ERROR, ...args));
    
    // Report to error reporting system if it's an Error object (lazy loaded to avoid circular dependency)
    if (args.length > 0 && args[0] instanceof Error) {
      // Lazy load errorReporting to break circular dependency
      import('./errorReporting').then(module => {
        module.errorReporting.reportError(args[0], {
          additionalArgs: args.slice(1),
          source: 'logger'
        }).catch(err => {
          // Silently fail error reporting to avoid infinite loops
          console.error('Failed to report error to error reporting system:', err);
        });
      }).catch(() => {
        // Silently fail if errorReporting can't be loaded
      });
    }
  }

  /**
   * Check if in development mode (inline to avoid circular dependency)
   */
  _isDevelopment() {
    try {
      if (import.meta.env && import.meta.env.DEV) {
        return true;
      } else if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        return hostname === 'localhost' || 
               hostname === '127.0.0.1' || 
               hostname.startsWith('192.168.') ||
               hostname.endsWith('.local');
      }
    } catch (error) {
      // Fallback
    }
    return false;
  }

  /**
   * Log with grouping (for complex objects)
   */
  group(label, callback) {
    if (!this.shouldLog(LOG_LEVELS.DEBUG) || !this._isDevelopment()) {
      if (callback) callback();
      return;
    }

    console.group(`${this.prefix} ${label}`);
    if (callback) {
      callback();
      console.groupEnd();
    }
  }

  /**
   * Log with grouping end
   */
  groupEnd() {
    if (this._isDevelopment()) {
      console.groupEnd();
    }
  }

  /**
   * Log table data
   */
  table(data) {
    if (!this.shouldLog(LOG_LEVELS.DEBUG) || !this._isDevelopment()) return;
    console.table(data);
  }

  /**
   * Time a function or operation
   */
  time(label) {
    if (!this.shouldLog(LOG_LEVELS.DEBUG) || !this._isDevelopment()) return;
    console.time(`${this.prefix} ${label}`);
  }

  /**
   * End timing
   */
  timeEnd(label) {
    if (!this.shouldLog(LOG_LEVELS.DEBUG) || !this._isDevelopment()) return;
    console.timeEnd(`${this.prefix} ${label}`);
  }
}

// Create singleton instance immediately
// Safe to do now since we removed circular dependencies (environmentConfig, errorReporting)
export const logger = new Logger();

// Convenience exports for direct use
export const log = logger.info.bind(logger);
export const logDebug = logger.debug.bind(logger);
export const logInfo = logger.info.bind(logger);
export const logWarn = logger.warn.bind(logger);
export const logError = logger.error.bind(logger);

// Backward compatibility - allows replacing console.log directly
export default logger;

